import { Box, Typography } from "@mui/material";
import type { FC } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/contexts/AuthProvider";
import { useAnalytics } from "@/contexts/analytics/hooks";
import { useChosenDepartment } from "@/contexts/ChosenDepartment";
import { WEEKDAY_ABBREVIATION_LABEL_KEY } from "@/pages/ActivityLog/GroupedActivityLog/activityLogGroupedDisplay";
import { FONT_SIZE, ICON_SIZE, INK } from "@/theme/tokens";
import { TagChip } from "@/ui/Badges/TagChip";
import { CopyButton } from "@/ui/molecules/CopyButton";
import { getTemplateIdEntries, type SingleRecurringShift, type TemplateIdFormat } from "./utils";

/** Stated once for the whole line, so the chips only carry what differs between them: weekday and id. */
const FORMAT_LABEL_KEY: Record<TemplateIdFormat, string> = {
	perDay: "recurring.shift.template.ids.per.day",
	wholeWeek: "recurring.shift.template.id.whole.week",
};

/** Per-entry accessible name, so a screen reader can tell side-by-side copy buttons apart. */
const FORMAT_COPY_LABEL_KEY: Record<TemplateIdFormat, string> = {
	perDay: "recurring.shift.template.id.copy.per.day",
	wholeWeek: "recurring.shift.template.id.copy.whole.week",
};

const captionSx = { fontSize: FONT_SIZE.CAPTION, color: INK.MUTED, whiteSpace: "nowrap" } as const;

type Props = {
	shift: SingleRecurringShift;
	weekStartDay: number;
};

/** Support-only: the `recurring_shift` id(s) behind a panel line as one compact metadata line, so Ops can look the template up. */
export const RecurringTemplateIds: FC<Props> = ({ shift, weekStartDay }) => {
	const { t } = useTranslation();
	const { userInfo } = useAuth();
	const { track } = useAnalytics();
	const { chosenDepartment } = useChosenDepartment();
	if (!userInfo.support) return null;

	const { format, entries } = getTemplateIdEntries(shift, weekStartDay);
	if (entries.length === 0) return null;
	const weekdaysLabel = (weekdays: string[]): string =>
		weekdays
			.map((weekday) => {
				const key = WEEKDAY_ABBREVIATION_LABEL_KEY[weekday];
				if (key === undefined) throw new Error(`RecurringTemplateIds: unknown weekday "${weekday}"`);
				return t(key);
			})
			.join(", ");

	return (
		<Box
			data-testid="recurring-shift-template-ids"
			sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", columnGap: 1.5, rowGap: 0.5, flexBasis: "100%" }}
		>
			<Typography component="span" sx={captionSx}>
				{format === null ? t("recurring.shift.template.id") : t(FORMAT_LABEL_KEY[format], { count: entries.length })}
			</Typography>
			{entries.map((entry) => (
				<Box key={entry.id} data-testid={`recurring-shift-template-id-${entry.id}`} sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}>
					{format !== null && (
						<Typography component="span" sx={captionSx}>
							{weekdaysLabel(entry.weekdays)}
						</Typography>
					)}
					<TagChip label={String(entry.id)} mono />
					<CopyButton
						value={String(entry.id)}
						label={format === null ? t("recurring.shift.template.id.copy") : t(FORMAT_COPY_LABEL_KEY[format], { weekdays: weekdaysLabel(entry.weekdays) })}
						size="dense"
						iconFontSize={ICON_SIZE.MICRO}
						onCopied={() => track("recurring_template_id_copied", { department_id: chosenDepartment.department_id, recurring_shift_id: entry.id })}
						dataTestId={`recurring-shift-template-id-${entry.id}-copy`}
					/>
				</Box>
			))}
		</Box>
	);
};
